import React, { useState, useEffect, useRef } from "react";
import {
  X, Plus, Minus, Search, Check, ChevronLeft, Loader2,
  Dumbbell,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

// Normalize grupo_muscular: production stores it as "Femoral, Glúteo" (string),
// preview as ["Bíceps"] (array). Always return an array.
function normalizeGrupos(g) {
  if (!g) return [];
  if (Array.isArray(g)) return g.filter(Boolean);
  if (typeof g === "string") return g.split(/,\s*/).filter(Boolean);
  return [];
}

async function fetchEjercicios() {
  try {
    const r = await fetch(`${API}/api/ejercicios`);
    if (r.ok) {
      const data = await r.json();
      const arr = Array.isArray(data) ? data : [];
      return arr.map((ex) => ({ ...ex, grupo_muscular: normalizeGrupos(ex.grupo_muscular) }));
    }
  } catch { /* noop */ }
  return [];
}

function ExerciseRow({ ej, onRemove }) {
  const hasMaq = !!ej.numero_maquina;
  const grupo = ej.grupo_muscular?.[0];
  return (
    <div className="flex items-center gap-3 py-2.5 border-b border-white/6 last:border-0">
      {hasMaq ? (
        <div className="shrink-0 flex flex-col items-center justify-center w-10 h-10 bg-[#F2C230] text-black">
          <span className="font-black text-[8px] leading-none">MÁQ</span>
          <span className="font-black text-sm leading-none">{ej.numero_maquina}</span>
        </div>
      ) : (
        <div className="shrink-0 w-10 h-10 flex items-center justify-center border border-white/12 bg-white/4">
          <Dumbbell size={14} className="text-white/35" />
        </div>
      )}
      <div className="flex-1 min-w-0">
        <p className="text-sm font-bold text-white truncate">{ej.nombre}</p>
        {grupo && (
          <span className="inline-block text-[9px] font-black uppercase tracking-wider text-[#F2C230]/70 mt-0.5">{grupo}</span>
        )}
      </div>
      <span className="text-xs font-black text-white/60 shrink-0">{ej.series}×{ej.repeticiones}</span>
      <button onClick={onRemove} className="shrink-0 w-7 h-7 flex items-center justify-center text-white/20 hover:text-red-400 transition-colors" data-testid="remove-exercise-btn">
        <X size={13} />
      </button>
    </div>
  );
}

function AddExerciseForm({ dayNum, exercises = [], onAdd, onClose }) {
  const [query, setQuery] = useState("");
  const [muscleFilter, setMuscleFilter] = useState(null);
  const [selected, setSelected] = useState(null);
  const [series, setSeries] = useState("3");
  const [reps, setReps] = useState("12");
  const inputRef = useRef(null);

  useEffect(() => { inputRef.current?.focus(); }, []);

  const safeExercises = Array.isArray(exercises) ? exercises : [];

  // Unique muscle groups from all exercises
  const muscleGroups = [...new Set(safeExercises.flatMap((e) => e.grupo_muscular || []))].sort();

  // Filtered suggestions
  const suggestions = safeExercises.filter((e) => {
    const matchesQuery = !query.trim() ||
      e.nombre?.toLowerCase().includes(query.toLowerCase()) ||
      (e.grupo_muscular || []).some((g) => g.toLowerCase().includes(query.toLowerCase()));
    const matchesMuscle = !muscleFilter || (e.grupo_muscular || []).includes(muscleFilter);
    return matchesQuery && matchesMuscle && e.activa !== false;
  }).slice(0, 30);

  const handleSelect = (ex) => {
    setSelected(ex);
    setQuery(ex.nombre);
  };

  const handleAdd = () => {
    const nombre = selected ? selected.nombre : query.trim();
    if (!nombre) return;
    onAdd({
      nombre,
      numero_maquina: selected?.numero_maquina || null,
      grupo_muscular: selected?.grupo_muscular || [],
      series: Math.max(1, parseInt(series) || 3),
      repeticiones: reps.trim() || "10",
    });
  };

  const valid = (selected ? selected.nombre : query.trim()).length > 0;
  const isFreeText = query.trim() && !selected;

  return (
    <div className="mt-2 p-4 bg-[#111] border border-white/10" data-testid="add-exercise-form">

      {/* Search */}
      <div className="relative mb-2">
        <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/30" />
        <input
          ref={inputRef}
          value={query}
          onChange={(e) => { setQuery(e.target.value); setSelected(null); }}
          placeholder="Buscar ejercicio o escribir uno libre…"
          data-testid="exercise-search-input"
          className="w-full bg-[#1a1a1a] border border-white/15 text-white pl-9 pr-4 py-2.5 text-sm outline-none focus:border-[#F2C230] transition-colors placeholder:text-white/25"
        />
      </div>

      {/* Muscle group filter pills */}
      {muscleGroups.length > 0 && !query.trim() && (
        <div className="flex flex-wrap gap-1.5 mb-3">
          {muscleGroups.map((g) => (
            <button key={g} onClick={() => setMuscleFilter(muscleFilter === g ? null : g)}
              data-testid={`filter-${g}`}
              className={`text-[10px] font-black uppercase tracking-wider px-2.5 py-1 transition-colors ${muscleFilter === g ? "bg-[#F2C230] text-black" : "border border-white/15 text-white/40 hover:border-[#F2C230]/50 hover:text-[#F2C230]"}`}>
              {g}
            </button>
          ))}
        </div>
      )}

      {/* Suggestions list */}
      {!selected && suggestions.length > 0 && (
        <div className="border border-white/10 mb-3 divide-y divide-white/6 max-h-52 overflow-y-auto overscroll-contain">
          {suggestions.map((ex) => (
            <button key={ex.id} onClick={() => handleSelect(ex)}
              data-testid={`suggestion-${ex.id}`}
              className="w-full flex items-center gap-2.5 px-3 py-2.5 hover:bg-white/5 transition-colors text-left">
              {ex.numero_maquina ? (
                <div className="shrink-0 w-8 h-8 flex flex-col items-center justify-center bg-[#F2C230]/12 text-[#F2C230]">
                  <span className="font-black text-[7px] leading-none">MÁQ</span>
                  <span className="font-black text-[11px] leading-none">{ex.numero_maquina}</span>
                </div>
              ) : (
                <div className="shrink-0 w-8 h-8 flex items-center justify-center border border-white/10 bg-white/3">
                  <Dumbbell size={12} className="text-white/30" />
                </div>
              )}
              <div className="min-w-0 flex-1">
                <p className="text-sm text-white font-bold truncate leading-tight">{ex.nombre}</p>
                {(ex.grupo_muscular || []).length > 0 && (
                  <p className="text-[10px] text-[#F2C230]/60 font-black uppercase tracking-wider mt-0.5">{ex.grupo_muscular.join(" · ")}</p>
                )}
              </div>
              {selected?.id === ex.id && <Check size={13} className="shrink-0 ml-auto text-[#F2C230]" />}
            </button>
          ))}
        </div>
      )}

      {/* Selected confirmation */}
      {selected && (
        <div className="flex items-center justify-between mb-3 px-3 py-2 bg-[#F2C230]/8 border border-[#F2C230]/20">
          <div className="min-w-0 flex-1">
            <p className="text-sm font-bold text-white truncate">{selected.nombre}</p>
            {(selected.grupo_muscular || []).length > 0 && (
              <p className="text-[10px] text-[#F2C230]/70 font-black uppercase tracking-wider">{selected.grupo_muscular.join(" · ")}</p>
            )}
          </div>
          <button onClick={() => { setSelected(null); setQuery(""); }} className="shrink-0 ml-2 text-white/30 hover:text-white transition-colors"><X size={13} /></button>
        </div>
      )}

      {/* Free-text notice */}
      {isFreeText && !suggestions.length && (
        <div className="flex items-center gap-1.5 mb-3 text-[11px] text-white/40">
          <Dumbbell size={11} />
          <span>Se añadirá como ejercicio libre: <strong className="text-white/65">"{query.trim()}"</strong></span>
        </div>
      )}

      {/* Series + Reps */}
      <div className="grid grid-cols-2 gap-3 mb-3">
        <div>
          <label className="block text-[10px] font-black tracking-[0.2em] uppercase text-white/40 mb-1">Series</label>
          <div className="flex items-center gap-1.5">
            <button onClick={() => setSeries((v) => String(Math.max(1, (parseInt(v) || 3) - 1)))}
              className="w-8 h-8 border border-white/12 flex items-center justify-center text-white/40 hover:text-white transition-colors"><Minus size={13} /></button>
            <input type="number" min="1" max="20" value={series} onChange={(e) => setSeries(e.target.value)}
              data-testid="add-series-input"
              className="flex-1 bg-[#1a1a1a] border border-white/15 text-white text-center py-1.5 text-sm font-black outline-none focus:border-[#F2C230] [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none" />
            <button onClick={() => setSeries((v) => String(Math.min(20, (parseInt(v) || 3) + 1)))}
              className="w-8 h-8 border border-white/12 flex items-center justify-center text-white/40 hover:text-white transition-colors"><Plus size={13} /></button>
          </div>
        </div>
        <div>
          <label className="block text-[10px] font-black tracking-[0.2em] uppercase text-white/40 mb-1">Reps / texto</label>
          <input type="text" value={reps} onChange={(e) => setReps(e.target.value)}
            placeholder="12 ó 8-12"
            data-testid="add-reps-input"
            className="w-full bg-[#1a1a1a] border border-white/15 text-white text-center py-2 text-sm font-black outline-none focus:border-[#F2C230] placeholder:text-white/20" />
        </div>
      </div>

      <div className="flex gap-2">
        <button onClick={onClose}
          className="flex-1 py-2.5 border border-white/12 text-white/40 font-black text-[11px] tracking-[0.15em] uppercase hover:border-white/25 hover:text-white transition-colors">
          Cancelar
        </button>
        <button onClick={handleAdd} disabled={!valid} data-testid="confirm-add-exercise-btn"
          className="flex-1 py-2.5 bg-[#F2C230] text-black font-black text-[11px] tracking-[0.15em] uppercase hover:bg-white transition-colors disabled:opacity-30">
          <Plus size={13} className="inline mr-1" /> Añadir
        </button>
      </div>
    </div>
  );
}

export default function RoutineBuilder({ initialData, onSave, onCancel, saving }) {
  const isEdit = !!initialData?.id;
  const [nombre, setNombre] = useState(initialData?.nombre || "");
  const [dias, setDias] = useState(initialData?.dias_por_semana || 3);
  const [nameErr, setNameErr] = useState("");

  // ejerciciosByDay: { 1: [...], 2: [...], ... }
  const [ejerciciosByDay, setEjerciciosByDay] = useState(() => {
    const init = {};
    for (let d = 1; d <= (initialData?.dias_por_semana || 3); d++) init[d] = [];
    if (initialData?.ejercicios) {
      for (const ej of initialData.ejercicios) {
        const day = ej.dia;
        if (!init[day]) init[day] = [];
        init[day].push(ej);
      }
      // Sort by orden
      for (const d of Object.keys(init)) init[d].sort((a, b) => (a.orden || 0) - (b.orden || 0));
    }
    return init;
  });

  const [addingToDay, setAddingToDay] = useState(null);
  const [exercises, setExercises] = useState([]);

  useEffect(() => {
    fetchEjercicios().then((data) => setExercises(Array.isArray(data) ? data : []));
  }, []);

  const handleDiasChange = (n) => {
    setDias(n);
    setEjerciciosByDay((prev) => {
      const next = {};
      for (let d = 1; d <= n; d++) next[d] = prev[d] || [];
      return next;
    });
    if (addingToDay > n) setAddingToDay(null);
  };

  const addExercise = (day, { nombre, numero_maquina, planta, series, repeticiones }) => {
    const orden = (ejerciciosByDay[day]?.length || 0) + 1;
    setEjerciciosByDay((prev) => ({
      ...prev,
      [day]: [...(prev[day] || []), { dia: day, orden, nombre, numero_maquina, planta, series, repeticiones }],
    }));
    setAddingToDay(null);
  };

  const removeExercise = (day, idx) => {
    setEjerciciosByDay((prev) => ({
      ...prev,
      [day]: prev[day].filter((_, i) => i !== idx).map((e, i) => ({ ...e, orden: i + 1 })),
    }));
  };

  const handleSave = () => {
    if (!nombre.trim()) { setNameErr("Introduce un nombre para la rutina"); return; }
    setNameErr("");
    const ejercicios = [];
    for (let d = 1; d <= dias; d++) {
      for (const ej of (ejerciciosByDay[d] || [])) {
        ejercicios.push({
          dia: ej.dia,
          orden: ej.orden,
          nombre: ej.nombre,
          numero_maquina: ej.numero_maquina || null,
          series: ej.series,
          repeticiones: String(ej.repeticiones),
        });
      }
    }
    onSave({ nombre: nombre.trim(), dias_por_semana: dias, ejercicios });
  };

  const totalExs = Object.values(ejerciciosByDay).flat().length;

  return (
    <div className="min-h-full flex flex-col" data-testid="routine-builder">

      {/* Sticky header */}
      <div className="sticky top-0 z-10 bg-[#0a0a0a] border-b border-white/8 px-4 py-4 flex items-center gap-3">
        <button onClick={onCancel} className="w-9 h-9 flex items-center justify-center border border-white/12 text-white/35 hover:text-white hover:border-white/25 transition-colors shrink-0">
          <ChevronLeft size={16} />
        </button>
        <div className="flex-1 min-w-0">
          <p className="text-[10px] font-black tracking-[0.2em] uppercase text-[#F2C230]">
            {isEdit ? "Editar rutina" : "Nueva rutina"}
          </p>
          <p className="text-xs text-white/30 mt-0.5 truncate">
            {totalExs} ejercicio{totalExs !== 1 ? "s" : ""} · {dias} día{dias !== 1 ? "s" : ""}
          </p>
        </div>
        <button onClick={handleSave} disabled={saving} data-testid="save-routine-btn"
          className="shrink-0 inline-flex items-center gap-2 px-5 py-2.5 bg-[#F2C230] text-black font-black text-[11px] tracking-[0.2em] uppercase hover:bg-white transition-colors disabled:opacity-40">
          {saving ? <Loader2 size={13} className="animate-spin" /> : <Check size={13} strokeWidth={3} />}
          Guardar
        </button>
      </div>

      <div className="flex-1 px-4 py-5 space-y-6 max-w-2xl mx-auto w-full">

        {/* Routine name */}
        <div>
          <label className="block text-[10px] font-black tracking-[0.2em] uppercase text-white/50 mb-2">Nombre de la rutina</label>
          <input
            type="text"
            value={nombre}
            onChange={(e) => { setNombre(e.target.value); setNameErr(""); }}
            placeholder="Ej: Mi push-pull-legs"
            maxLength={80}
            data-testid="routine-name-input"
            className="w-full bg-[#111] border border-white/15 text-white px-4 py-3 text-sm font-bold outline-none focus:border-[#F2C230] transition-colors placeholder:text-white/20"
          />
          {nameErr && <p className="text-red-400 text-xs mt-1">{nameErr}</p>}
        </div>

        {/* Days selector */}
        <div>
          <label className="block text-[10px] font-black tracking-[0.2em] uppercase text-white/50 mb-2">Días por semana</label>
          <div className="flex gap-2">
            {[1, 2, 3, 4, 5, 6].map((n) => (
              <button key={n} onClick={() => handleDiasChange(n)} data-testid={`dias-btn-${n}`}
                className={`w-10 h-10 font-black text-sm border transition-colors ${dias === n ? "bg-[#F2C230] text-black border-[#F2C230]" : "border-white/12 text-white/50 hover:border-white/25 hover:text-white"}`}>
                {n}
              </button>
            ))}
          </div>
        </div>

        {/* Days list */}
        {Array.from({ length: dias }, (_, i) => i + 1).map((day) => {
          const exs = ejerciciosByDay[day] || [];
          const isAddingHere = addingToDay === day;
          return (
            <div key={day} data-testid={`day-section-${day}`}>
              <div className="flex items-center justify-between mb-2">
                <p className="text-[10px] font-black tracking-[0.2em] uppercase text-[#F2C230]">Día {day}</p>
                <span className="text-[10px] text-white/25">{exs.length} ejercicio{exs.length !== 1 ? "s" : ""}</span>
              </div>

              <div className="border border-white/8">
                {exs.length > 0 && (
                  <div className="px-4 divide-y divide-white/5">
                    {exs.map((ej, i) => (
                      <ExerciseRow key={i} ej={ej} onRemove={() => removeExercise(day, i)} />
                    ))}
                  </div>
                )}

                {!isAddingHere && (
                  <button
                    onClick={() => setAddingToDay(day)}
                    data-testid={`add-exercise-day-${day}`}
                    className={`w-full flex items-center justify-center gap-2 py-3 text-[11px] font-black tracking-[0.15em] uppercase transition-colors ${exs.length > 0 ? "border-t border-white/6 text-white/30 hover:text-[#F2C230]" : "text-white/25 hover:text-[#F2C230]"}`}>
                    <Plus size={13} /> Añadir ejercicio
                  </button>
                )}

                {isAddingHere && (
                  <div className="border-t border-white/8 p-3">
                    <AddExerciseForm
                      dayNum={day}
                      exercises={exercises}
                      onAdd={(data) => addExercise(day, data)}
                      onClose={() => setAddingToDay(null)}
                    />
                  </div>
                )}
              </div>
            </div>
          );
        })}

        {/* Bottom save */}
        <div className="pt-2 pb-8">
          <button onClick={handleSave} disabled={saving} data-testid="save-routine-bottom-btn"
            className="w-full py-4 bg-[#F2C230] text-black font-black text-sm tracking-[0.2em] uppercase hover:bg-white transition-colors disabled:opacity-40 flex items-center justify-center gap-2">
            {saving ? <Loader2 size={16} className="animate-spin" /> : null}
            {isEdit ? "Guardar cambios" : "Crear rutina"}
          </button>
          <button onClick={onCancel} className="w-full py-3 mt-2 text-white/35 font-black text-[11px] tracking-[0.2em] uppercase hover:text-white transition-colors">
            Cancelar
          </button>
        </div>

      </div>
    </div>
  );
}
